import React, { useState, useRef, useEffect, useCallback } from 'react';
import './AIChatAgent.css';
import VoiceMode from './VoiceMode';
import AnatomyPanel from './AnatomyPanel';
import { useAuth } from '../context/AuthContext';

// Conversation Mode pills. Each color is referenced via a CSS variable
// (defined in AIChatAgent.css) with a literal fallback, so the pills respect
// both the light and dark themes. Active key is sent to the backend as `mode`.
const MODE_CONFIG = [
  {
    key: 'resolution',
    label: 'Resolution',
    emoji: '✅',
    description: 'Help me solve this',
    colorVar: '--mode-resolution',
    colorFallback: '#22c55e',       // green
  },
  {
    key: 'vent',
    label: 'Vent',
    emoji: '🫂',
    description: 'I just need to be heard',
    colorVar: '--mode-vent',
    colorFallback: '#a78bfa',       // soft purple
  },
  {
    key: 'reflection',
    label: 'Reflection',
    emoji: '🪞',
    description: 'Help me understand',
    colorVar: '--mode-reflection',
    colorFallback: '#38bdf8',       // calm blue
  },
  {
    key: 'challenge',
    label: 'Challenge',
    emoji: '🔥',
    description: 'Give me tough love',
    colorVar: '--mode-challenge',
    colorFallback: '#f97316',       // bold orange
  },
];

// Loader shown while Elo is thinking: a single thread that continuously
// untangles itself into an inward spiral and re-tangles on loop.
// Uses SMIL <animate> to morph the `d` attribute between two paths that share
// the same command structure (M + 6 cubic beziers) so the tween is smooth.
const TANGLE_D =
  'M 52 32 C 30 0 14 60 44 14 C 10 10 56 44 16 42 C 52 50 8 24 40 50 C 22 10 50 28 12 30 C 44 8 18 56 48 38 C 28 60 4 20 36 32';
const SPIRAL_D =
  'M 52 32 C 52 8 12 8 12 32 C 12 54 48 54 48 32 C 48 14 18 14 18 32 C 18 46 42 46 42 32 C 42 22 24 22 24 32 C 24 38 36 38 36 32';

const ThinkingLoader = () => (
  <div className="chat-message assistant Elo-thinking-row">
    <div className="message-avatar">E</div>
    <div className="Elo-thinking">
      <svg
        className="thread-loader"
        viewBox="0 0 64 64"
        aria-hidden="true"
        focusable="false"
      >
        <path className="thread-path" d={TANGLE_D}>
          <animate
            attributeName="d"
            dur="3.6s"
            repeatCount="indefinite"
            values={`${TANGLE_D};${SPIRAL_D};${SPIRAL_D};${TANGLE_D}`}
            keyTimes="0; 0.45; 0.65; 1"
            calcMode="spline"
            keySplines="0.45 0 0.2 1; 0 0 1 1; 0.55 0 0.3 1"
          />
        </path>
      </svg>
      <span className="Elo-thinking-label">Elo is untangling your thoughts</span>
    </div>
  </div>
);

const AIChatAgent = ({ 
  numerologyData, 
  embedded = false, 
  messages: externalMessages,
  onMessagesChange,
  selectedContact,
  contactNumerology,
  selectedTeam,
  teamMembers,
  teamNumerologies,
  currentChatId,
  onNewChat,
  lifeContext,
  onLifeContextUpdate,
  contactInsights
}) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(embedded);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isVoiceModeOpen, setIsVoiceModeOpen] = useState(false);
  const [anatomyMessageId, setAnatomyMessageId] = useState(null);
  const [activeMode, setActiveMode] = useState(null);
  // Mode tint — subtly colors the chat background to match the active mode.
  // User-toggleable; preference persists across sessions in localStorage.
  // Defaults to ON; user opts OUT by writing 'off'.
  const [tintEnabled, setTintEnabled] = useState(() => {
    try { return localStorage.getItem('modeTint') !== 'off'; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('modeTint', tintEnabled ? 'on' : 'off'); }
    catch { /* localStorage blocked — preference becomes session-only */ }
  }, [tintEnabled]);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const userScrolledRef = useRef(false);
  const abortControllerRef = useRef(null);
  const isSendingRef = useRef(false);
  
  // Messages state - always use externalMessages as the source of truth
  const [messages, setMessages] = useState(() => {
    if (Array.isArray(externalMessages) && externalMessages.length > 0) {
      return externalMessages;
    }
    return [];
  });
  
  // Track previous chatId to detect changes
  const prevChatIdRef = useRef(currentChatId);
  
  // CRITICAL: Sync messages whenever externalMessages prop changes
  // This handles both loading existing chats and real-time updates
  useEffect(() => {
    // Detect if chatId changed
    const chatIdChanged = prevChatIdRef.current !== currentChatId;
    prevChatIdRef.current = currentChatId;
    
    if (Array.isArray(externalMessages) && externalMessages.length > 0) {
      // Have external messages - use them (e.g. loading a chat from sidebar)
      setMessages(externalMessages);
    } else if (chatIdChanged && !isSendingRef.current) {
      // Chat switched while NOT mid-send (user clicked New Chat or selected a different chat)
      // Reset conversation mode — each chat starts in default balanced style.
      setActiveMode(null);
      // Reset to welcome message
      setMessages([{
        role: 'assistant',
        content: selectedTeam && teamMembers && teamMembers.length > 0
          ? `Hi! I see you want to analyze **${selectedTeam.name}** (${teamMembers.length} members).\n\nI can help you understand:\n- Overall team dynamics\n- Compatibility between specific pairs\n- Role recommendations\n\n**What would you like to know about this team?**`
          : selectedContact
            ? `Hi! I see you want to talk about **${selectedContact.name}**${selectedContact.relation ? ` (${selectedContact.relation})` : ''}. What would you like to know about your dynamic with them?`
            : `Hi, I'm **Elo**. ✨\n\n**What brings you here today?**`
      }]);
    } else if (messages.length === 0 && !currentChatId && embedded) {
      // Initial empty state — show welcome
      setMessages([{
        role: 'assistant',
        content: `Hi, I'm **Elo**. ✨\n\n**What brings you here today?**`
      }]);
    }
  }, [externalMessages, currentChatId, embedded, selectedContact, selectedTeam, teamMembers]);

  // Helper function to add delay for slower streaming
  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const stripHiddenTags = (text) => {
    if (!text) return '';
    return text
      // Remove complete HTML comment blocks (covers LIFE_UPDATE / PERSONA_UPDATE and any variants)
      .replace(/<!--[\s\S]*?-->/g, '')
      // Remove any trailing partial comment block while streaming
      .replace(/<!--[\s\S]*$/g, '');
  };

  const parseMarkdown = (text) => {
    if (!text) return '';

    let html = text;

    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Blockquotes: > text
    html = html.replace(/^&gt;\s*(.+)$/gm, '<blockquote>$1</blockquote>');

    html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.*?)__/g, '<strong>$1</strong>');
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');

    const lines = html.split('\n');
    const processedLines = [];
    let inList = false;
    let listType = null;

    const closeList = () => {
      if (inList) {
        processedLines.push(listType === 'ul' ? '</ul>' : '</ol>');
        inList = false;
        listType = null;
      }
    };

    const isListLine = (line) => {
      return /^[\s]*[-•\*]\s+/.test(line) || /^[\s]*\d+[\.\)]\s+/.test(line);
    };

    // Look ahead past blank lines to find the next non-empty line.
    const nextNonEmpty = (startIdx) => {
      for (let j = startIdx; j < lines.length; j++) {
        if (lines[j].trim() !== '') return lines[j];
      }
      return '';
    };

    // Append continuation text to the most recent <li>.
    const appendToLastListItem = (text) => {
      const lastIdx = processedLines.length - 1;
      const lastItem = processedLines[lastIdx];
      if (lastItem && /<\/li>\s*$/.test(lastItem)) {
        processedLines[lastIdx] = lastItem.replace(/<\/li>\s*$/, ' ' + text + '</li>');
        return true;
      }
      return false;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Horizontal rule: --- or *** or ___
      if (/^[-*_]{3,}\s*$/.test(line.trim())) {
        closeList();
        processedLines.push('<hr class="chat-divider"/>');
        continue;
      }

      const h3Match = line.match(/^###\s+(.+)$/);
      const h2Match = line.match(/^##\s+(.+)$/);
      const h1Match = line.match(/^#\s+(.+)$/);
      const bulletMatch = line.match(/^[\s]*[-•\*]\s+(.+)$/);
      const numberedMatch = line.match(/^[\s]*(\d+)[\.\)]\s+(.+)$/);

      if (h3Match) {
        closeList();
        processedLines.push(`<h3>${h3Match[1]}</h3>`);
      } else if (h2Match) {
        closeList();
        processedLines.push(`<h2>${h2Match[1]}</h2>`);
      } else if (h1Match) {
        closeList();
        processedLines.push(`<h1>${h1Match[1]}</h1>`);
      } else if (bulletMatch) {
        if (!inList || listType !== 'ul') {
          closeList();
          processedLines.push('<ul class="chat-list">');
          inList = true;
          listType = 'ul';
        }
        processedLines.push(`<li>${bulletMatch[1]}</li>`);
      } else if (numberedMatch) {
        const num = parseInt(numberedMatch[1], 10);
        if (!inList || listType !== 'ol') {
          closeList();
          // start attribute preserves first item's number even if list later breaks
          processedLines.push(`<ol class="chat-list" start="${num}">`);
          inList = true;
          listType = 'ol';
        }
        // value attribute pins each item's number, surviving any list-restart
        processedLines.push(`<li value="${num}">${numberedMatch[2]}</li>`);
      } else if (line.trim() === '') {
        // If the next non-empty line continues the list, keep the list open
        // so consecutive numbered items render in a single <ol>.
        const nextLine = nextNonEmpty(i + 1);
        if (!inList || !isListLine(nextLine)) {
          closeList();
          const last = processedLines[processedLines.length - 1];
          if (last && !last.includes('chat-spacer')) {
            processedLines.push('<div class="chat-spacer"></div>');
          }
        }
      } else {
        // Inside a list, an un-indented continuation line (common from LLMs)
        // should fold into the previous <li> rather than terminate the list.
        if (inList && appendToLastListItem(line)) {
          continue;
        }
        closeList();
        processedLines.push(`<p>${line}</p>`);
      }
    }
    closeList();

    return processedLines.join('');
  };

  // Auto-scroll to bottom when new messages arrive (only if user hasn't scrolled up)
  const scrollToBottom = (force = false) => {
    if (force || !userScrolledRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Check if user is near the bottom of the chat
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    
    // If user scrolled more than 100px from bottom, they've manually scrolled up
    // If they're within 100px of bottom, consider them "at bottom"
    userScrolledRef.current = distanceFromBottom > 100;
  }, []);

  // Reset userScrolled when user sends a new message
  useEffect(() => {
    if (!isStreaming && !isLoading) {
      // When not streaming, auto-scroll is allowed again when user sends message
    }
  }, [isStreaming, isLoading]);

  useEffect(() => {
    // Only auto-scroll if user hasn't manually scrolled up
    if (!userScrolledRef.current) {
      scrollToBottom();
    }
  }, [messages]);
  
  // When user sends a message, always scroll to bottom
  const scrollToBottomOnSend = () => {
    userScrolledRef.current = false;
    scrollToBottom(true);
  };

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    if (!inputValue.trim()) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    isSendingRef.current = true;

    const userMessage = inputValue.trim();
    setInputValue('');
    scrollToBottomOnSend();

    const userMsg = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, userMsg]);
    setIsLoading(true);

    let chatId = currentChatId;

    try {
      const token = localStorage.getItem('token');

      const response = await fetch('/api/ai/chat', {
        method: 'POST',
        signal,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          chatId: chatId || null,
          contactId: selectedContact?.id || null,
          teamId: selectedTeam?.id || null,
          mode: activeMode,
          includeAttribution: user?.is_admin === true
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response from AI');
      }

      // Read chatId from header immediately — backend always sets this
      const returnedChatId = response.headers.get('X-Chat-Id');
      if (returnedChatId && returnedChatId !== chatId) {
        chatId = returnedChatId;
        localStorage.setItem('currentChatId', chatId);
        if (onMessagesChange) onMessagesChange(chatId);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let streamedContent = '';
      let streamedThinking = '';
      let pendingText = '';
      let pendingThinking = '';
      let hasAddedMessage = false;

      while (true) {
        if (signal.aborted) break;
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            try {
              const parsed = JSON.parse(data);
              if (parsed.type === 'response.reasoning_summary_text.delta') {
                pendingThinking += parsed.delta || '';
              }
              if (parsed.type === 'response.output_text.delta') {
                pendingText += parsed.delta || '';
              }
            } catch {}
          }
        }

        if (!hasAddedMessage && (pendingThinking.length > 0 || pendingText.length > 0)) {
          hasAddedMessage = true;
          setIsLoading(false);
          setIsStreaming(true);
          setMessages(prev => [...prev, { role: 'assistant', content: '', thinking: '' }]);
        }

        while (pendingThinking.length > 0 && !signal.aborted) {
          const char = pendingThinking[0];
          pendingThinking = pendingThinking.slice(1);
          streamedThinking += char;
          setMessages(prev => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = { role: 'assistant', content: streamedContent, thinking: streamedThinking };
            return newMessages;
          });
          await delay(5);
        }

        let lastRendered = '';
        while (pendingText.length > 0 && !signal.aborted) {
          const batchSize = Math.min(3, pendingText.length);
          const batch = pendingText.slice(0, batchSize);
          pendingText = pendingText.slice(batchSize);
          streamedContent += batch;

          const displayContent = stripHiddenTags(streamedContent)
            .replace(/<!-?-?[A-Z]{0,20}$/, '');

          if (displayContent === lastRendered) continue;
          lastRendered = displayContent;

          setMessages(prev => {
            const newMessages = [...prev];
            newMessages[newMessages.length - 1] = { role: 'assistant', content: displayContent, thinking: streamedThinking };
            return newMessages;
          });
          await delay(10);
        }
      }

      setIsLoading(false);
      setIsStreaming(false);
      if (signal.aborted) return;

      // Clean final content (tags are processed server-side now)
      const finalContent = stripHiddenTags(streamedContent).trim();

      setMessages(prev => {
        const newMessages = [...prev];
        newMessages[newMessages.length - 1] = { role: 'assistant', content: finalContent, thinking: streamedThinking };
        return newMessages;
      });

      // Refresh chats list to update sidebar timestamps/titles
      if (onMessagesChange) onMessagesChange(null);

    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error sending message:', error);
      const errorContent = 'I apologize, but I encountered an error. Please try again.';
      setMessages(prev => {
        const newMessages = [...prev];
        if (newMessages.length > 0 && newMessages[newMessages.length - 1].role === 'assistant') {
          newMessages[newMessages.length - 1] = { role: 'assistant', content: errorContent };
        } else {
          newMessages.push({ role: 'assistant', content: errorContent });
        }
        return newMessages;
      });
    } finally {
      isSendingRef.current = false;
      if (!signal.aborted) {
        setIsLoading(false);
        setIsStreaming(false);
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: `Hi, I'm **Elo**. ✨\n\n**What brings you here today?**`
      }]);
    }
  };

  const clearSession = () => {
    // New chat → drop any active conversation mode so we start neutral.
    setActiveMode(null);
    if (onNewChat) {
      onNewChat();
    } else {
      setMessages([{
        role: 'assistant',
        content: getWelcomeMessage()
      }]);
    }
  };

  // Get welcome message based on context
  const getWelcomeMessage = () => {
    if (selectedTeam && teamMembers && teamMembers.length > 0) {
      const memberNames = teamMembers.slice(0, 3).map(m => m.name).join(', ');
      const moreCount = teamMembers.length > 3 ? ` and ${teamMembers.length - 3} more` : '';
      return `Hi! I see you want to analyze **${selectedTeam.name}** (${teamMembers.length} members: ${memberNames}${moreCount}).\n\nI can help you understand:\n- Overall team dynamics\n- Compatibility between specific pairs\n- Role recommendations\n\n**What would you like to know about this team?**`;
    }
    if (selectedContact) {
      return `Hi! I see you want to talk about **${selectedContact.name}**${selectedContact.relation ? ` (${selectedContact.relation})` : ''}. What would you like to know about your dynamic with them?`;
    }
    return `Hi, I'm **Elo**. ✨\n\n**What brings you here today?**`;
  };

  // No longer need separate welcome message effect - handled in initialization

  // Embedded mode - renders directly in column
  if (embedded) {
    const mainGrid = numerologyData?.mainGrid || [];
    
    return (
      <div
        className="chat-embedded"
        data-mode={tintEnabled && activeMode ? activeMode : undefined}
      >
        {/* Header */}
        <div className="column-header-chat">
          <div className="header-left">
            <h2>Elo</h2>
            <div className="subtitle">
              {selectedTeam
                ? `Analyzing: ${selectedTeam.name} (${teamMembers?.length || 0} members)`
                : selectedContact
                  ? `Chatting about ${selectedContact.name}`
                  : 'Your Personal Life Guide'}
            </div>
            {activeMode && (() => {
              const m = MODE_CONFIG.find(m => m.key === activeMode);
              return m ? (
                <span
                  className="mode-indicator"
                  style={{ color: `var(${m.colorVar}, ${m.colorFallback})` }}
                >
                  {m.emoji} {m.label} mode
                </span>
              ) : null;
            })()}
          </div>
          
          <div className="header-right">
            {/* User's Grid Display (admin-only) */}
            {user?.is_admin && mainGrid && mainGrid.length > 0 && (
              <div className="header-main-grid" title="Your grid">
                {mainGrid.map((number, index) => (
                  <div key={`header-main-${index}`} className="header-grid-number">
                    {number}
                  </div>
                ))}
              </div>
            )}

            {/* Contact's Grid Display (admin-only) */}
            {user?.is_admin && selectedContact && contactNumerology?.mainGrid && !selectedTeam && (
              <div className="header-contact-grid" title={`${selectedContact.name}'s grid`}>
                <span className="contact-grid-label">{selectedContact.name.split(' ')[0]}:</span>
                {contactNumerology.mainGrid.map((number, index) => (
                  <div key={`contact-main-${index}`} className="header-grid-number contact">
                    {number}
                  </div>
                ))}
              </div>
            )}
            
            {/* Team Display */}
            {selectedTeam && teamMembers && teamMembers.length > 0 && (
              <div className="header-team-info" title={`Team: ${selectedTeam.name}`}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="team-icon">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                <span className="team-label">{teamMembers.length}</span>
              </div>
            )}
            
            <button
              className={`mode-tint-toggle ${tintEnabled ? 'on' : 'off'}`}
              onClick={() => setTintEnabled(v => !v)}
              title={tintEnabled ? 'Mode tint: on (click to disable)' : 'Mode tint: off (click to enable)'}
              aria-pressed={tintEnabled}
              aria-label="Toggle mode tint"
              style={tintEnabled && activeMode ? (() => {
                const m = MODE_CONFIG.find(m => m.key === activeMode);
                return m ? { color: `var(${m.colorVar}, ${m.colorFallback})` } : null;
              })() : null}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 2.5c4 5 6 8.2 6 11a6 6 0 0 1-12 0c0-2.8 2-6 6-11z" />
              </svg>
            </button>
            <button
              className="chat-refresh-btn-embedded"
              onClick={clearSession}
              title="New chat"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages" ref={messagesContainerRef} onScroll={handleScroll}>
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`chat-message ${message.role === 'user' ? 'user' : 'assistant'}${isStreaming && message.role === 'assistant' && index === messages.length - 1 ? ' streaming' : ''}`}
            >
              {message.role === 'assistant' && (
                <div className="message-avatar">E</div>
              )}
              <div className="message-wrapper">
                {/* Thinking Section - Collapsible */}
                {message.role === 'assistant' && message.thinking && (
                  <details className="thinking-section">
                    <summary className="thinking-header">
                      <span className="thinking-icon">🧠</span>
                      <span>Thinking...</span>
                    </summary>
                    <div className="thinking-content">
                      {message.thinking}
                    </div>
                  </details>
                )}
                {/* Main Response */}
                <div
                  className="message-content"
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(stripHiddenTags(message.content)) }}
                />
                {/* Anatomy Button (admin-only) */}
                {user?.is_admin && message.role === 'assistant' && message.id && message.has_anatomy && (
                  <button
                    className="anatomy-btn"
                    onClick={() => setAnatomyMessageId(message.id)}
                    title="Why this answer?"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4M12 8h.01" />
                    </svg>
                    <span>Why this answer?</span>
                  </button>
                )}
              </div>
              {message.role === 'user' && (
                <div className="message-avatar user-avatar">You</div>
              )}
            </div>
          ))}
          {isLoading && !isStreaming && <ThinkingLoader />}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chat-input-area">
          <div className="mode-selector">
            {MODE_CONFIG.map(({ key, label, emoji, description, colorVar, colorFallback }) => {
              const isActive = activeMode === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`mode-pill mode-pill--${key} ${isActive ? 'mode-pill--active' : ''}`}
                  onClick={() => setActiveMode(prev => prev === key ? null : key)}
                  title={description}
                  style={isActive ? {
                    background: `var(${colorVar}, ${colorFallback})`,
                    borderColor: `var(${colorVar}, ${colorFallback})`,
                    color: '#ffffff',
                    boxShadow: `0 2px 12px var(${colorVar}40, ${colorFallback}40)`,
                  } : {
                    borderColor: `var(${colorVar}, ${colorFallback})`,
                    color: `var(${colorVar}, ${colorFallback})`,
                  }}
                >
                  <span className="mode-pill__emoji">{emoji}</span>
                  <span className="mode-pill__label">{label}</span>
                </button>
              );
            })}
          </div>
          <div className="chat-input-wrapper">
            <textarea
              ref={inputRef}
              className="chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about your life path..."
              rows="1"
              disabled={false}
            />
            <button
              className="chat-voice-btn"
              onClick={() => setIsVoiceModeOpen(true)}
              title="Voice mode"
              disabled={false}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
            <button
              className="chat-send-btn"
              onClick={sendMessage}
              disabled={!inputValue.trim()}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>

        {/* Voice Mode */}
        <VoiceMode
          isOpen={isVoiceModeOpen}
          onClose={() => setIsVoiceModeOpen(false)}
          systemPrompt=""
          numerologyContext=""
          chatMessages={messages}
          onNewMessage={(msg) => setMessages(prev => [...prev, msg])}
        />

        {/* Anatomy Panel (admin-only) */}
        {user?.is_admin && (
          <AnatomyPanel
            chatId={currentChatId}
            messageId={anatomyMessageId}
            isOpen={!!anatomyMessageId}
            onClose={() => setAnatomyMessageId(null)}
          />
        )}
      </div>
    );
  }

  // Floating mode - original behavior
  return (
    <>
      {/* Chat Bubble Button */}
      <button 
        className={`chat-bubble-btn ${isOpen ? 'active' : ''}`}
        onClick={toggleChat}
        aria-label="Open AI Chat"
      >
        {isOpen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
          </svg>
        )}
        <span className="chat-bubble-pulse"></span>
      </button>

      {/* Chat Window */}
      <div
        className={`chat-window ${isOpen ? 'open' : ''}`}
        data-mode={tintEnabled && activeMode ? activeMode : undefined}
      >
        {/* Header */}
        <div className="chat-header">
          <div className="chat-header-info">
            <div className="chat-avatar">
              <span>✨</span>
            </div>
            <div className="chat-header-text">
              <h3>Elo</h3>
              <span className="chat-status">PhD Psychology • Life Coach</span>
              {activeMode && (() => {
                const m = MODE_CONFIG.find(m => m.key === activeMode);
                return m ? (
                  <span
                    className="mode-indicator"
                    style={{ color: `var(${m.colorVar}, ${m.colorFallback})` }}
                  >
                    {m.emoji} {m.label} mode
                  </span>
                ) : null;
              })()}
            </div>
          </div>
          <div className="chat-header-actions">
            <button
              className={`mode-tint-toggle floating ${tintEnabled ? 'on' : 'off'}`}
              onClick={() => setTintEnabled(v => !v)}
              title={tintEnabled ? 'Mode tint: on (click to disable)' : 'Mode tint: off (click to enable)'}
              aria-pressed={tintEnabled}
              aria-label="Toggle mode tint"
              style={tintEnabled && activeMode ? (() => {
                const m = MODE_CONFIG.find(m => m.key === activeMode);
                return m ? { color: `var(${m.colorVar}, ${m.colorFallback})` } : null;
              })() : null}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M12 2.5c4 5 6 8.2 6 11a6 6 0 0 1-12 0c0-2.8 2-6 6-11z" />
              </svg>
            </button>
            <button
              className="chat-refresh-btn"
              onClick={clearSession}
              title="Start new session"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6M1 20v-6h6M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>
            </button>
            <button className="chat-close-btn" onClick={toggleChat}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="chat-messages" onScroll={handleScroll}>
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`chat-message ${message.role === 'user' ? 'user' : 'assistant'}${isStreaming && message.role === 'assistant' && index === messages.length - 1 ? ' streaming' : ''}`}
            >
              {message.role === 'assistant' && (
                <div className="message-avatar">E</div>
              )}
              <div className="message-wrapper">
                {/* Thinking Section - Collapsible */}
                {message.role === 'assistant' && message.thinking && (
                  <details className="thinking-section">
                    <summary className="thinking-header">
                      <span className="thinking-icon">🧠</span>
                      <span>Thinking...</span>
                    </summary>
                    <div className="thinking-content">
                      {message.thinking}
                    </div>
                  </details>
                )}
                {/* Main Response */}
                <div
                  className="message-content"
                  dangerouslySetInnerHTML={{ __html: parseMarkdown(stripHiddenTags(message.content)) }}
                />
                {/* Anatomy Button (admin-only) */}
                {user?.is_admin && message.role === 'assistant' && message.id && message.has_anatomy && (
                  <button
                    className="anatomy-btn"
                    onClick={() => setAnatomyMessageId(message.id)}
                    title="Why this answer?"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4M12 8h.01" />
                    </svg>
                    <span>Why this answer?</span>
                  </button>
                )}
              </div>
              {message.role === 'user' && (
                <div className="message-avatar user-avatar">You</div>
              )}
            </div>
          ))}
          {isLoading && !isStreaming && <ThinkingLoader />}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chat-input-area">
          <div className="mode-selector">
            {MODE_CONFIG.map(({ key, label, emoji, description, colorVar, colorFallback }) => {
              const isActive = activeMode === key;
              return (
                <button
                  key={key}
                  type="button"
                  className={`mode-pill mode-pill--${key} ${isActive ? 'mode-pill--active' : ''}`}
                  onClick={() => setActiveMode(prev => prev === key ? null : key)}
                  title={description}
                  style={isActive ? {
                    background: `var(${colorVar}, ${colorFallback})`,
                    borderColor: `var(${colorVar}, ${colorFallback})`,
                    color: '#ffffff',
                    boxShadow: `0 2px 12px var(${colorVar}40, ${colorFallback}40)`,
                  } : {
                    borderColor: `var(${colorVar}, ${colorFallback})`,
                    color: `var(${colorVar}, ${colorFallback})`,
                  }}
                >
                  <span className="mode-pill__emoji">{emoji}</span>
                  <span className="mode-pill__label">{label}</span>
                </button>
              );
            })}
          </div>
          <div className="chat-input-wrapper">
            <textarea
              ref={inputRef}
              className="chat-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about your life path..."
              rows="1"
              disabled={false}
            />
            <button
              className="chat-voice-btn"
              onClick={() => setIsVoiceModeOpen(true)}
              title="Voice mode"
              disabled={false}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
            <button
              className="chat-send-btn"
              onClick={sendMessage}
              disabled={!inputValue.trim()}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Voice Mode */}
      <VoiceMode
        isOpen={isVoiceModeOpen}
        onClose={() => setIsVoiceModeOpen(false)}
        systemPrompt=""
        numerologyContext=""
        chatMessages={messages}
        onNewMessage={(msg) => setMessages(prev => [...prev, msg])}
      />

      {/* Anatomy Panel (admin-only) */}
      {user?.is_admin && (
        <AnatomyPanel
          chatId={currentChatId}
          messageId={anatomyMessageId}
          isOpen={!!anatomyMessageId}
          onClose={() => setAnatomyMessageId(null)}
        />
      )}
    </>
  );
};

export default AIChatAgent;
